<template>
    <jet-form-section @submitted="updateProfileInformation">
        <template #title>
            Profile Information
        </template>

        <template #description>
            Update your account's profile information and email address.
        </template>

        <template #form>
            <!-- Profile Photo -->
            <div class="col-span-6 sm:col-span-4" v-if="safeJetstream.managesProfilePhotos">
                <!-- Profile Photo File Input -->
                <input type="file" class="hidden"
                            ref="photo"
                            @change="updatePhotoPreview">

                <jet-label for="photo" value="Photo" />

                <!-- Current Profile Photo -->
                <div class="mt-2" v-show="! photoPreview">
                    <img :src="profilePhotoSrc" alt="Current Profile Photo" class="rounded-full h-20 w-20 object-cover">
                </div>

                <!-- New Profile Photo Preview -->
                <div class="mt-2" v-show="photoPreview">
                    <span class="block rounded-full w-20 h-20"
                          :style="'background-size: cover; background-repeat: no-repeat; background-position: center center; background-image: url(\'' + photoPreview + '\');'">
                    </span>
                </div>

                <jet-secondary-button class="mt-2 mr-2" type="button" @click.native.prevent="selectNewPhoto">
                    Select A New Photo
                </jet-secondary-button>

                <jet-secondary-button type="button" class="mt-2" @click.native.prevent="deletePhoto" v-if="profilePhotoSrc">
                    Remove Photo
                </jet-secondary-button>

                <p class="mt-2 text-xs text-gray-500">
                    JPG/PNG/BMP, max 1MB. Professional face photo only.
                </p>

                <p class="mt-2 text-sm" :class="photoValidationClass" v-if="photoValidationMessage">
                    {{ photoValidationMessage }}
                </p>

                <jet-input-error :message="fieldError('photo')" class="mt-2" />
            </div>

            <!-- Name -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="name" value="Name" />
                <jet-input id="name" type="text" class="mt-1 block w-full" v-model="form.name" autocomplete="name" />
                <jet-input-error :message="fieldError('name')" class="mt-2" />
            </div>

            <!-- Email -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="email" value="Email" />
                <jet-input id="email" type="email" class="mt-1 block w-full" v-model="form.email" />
                <jet-input-error :message="fieldError('email')" class="mt-2" />
            </div>
        </template>

        <template #actions>
            <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                Saved.
            </jet-action-message>

            <!-- <jet-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                Save
            </jet-button> -->
            <v-btn type="submit" color="primary" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
               Save
            </v-btn>
        </template>
    </jet-form-section>
</template>

<script>
    import JetButton from '@/Jetstream/Button'
    import JetFormSection from '@/Jetstream/FormSection'
    import JetInput from '@/Jetstream/Input'
    import JetInputError from '@/Jetstream/InputError'
    import JetLabel from '@/Jetstream/Label'
    import JetActionMessage from '@/Jetstream/ActionMessage'
    import JetSecondaryButton from '@/Jetstream/SecondaryButton'

    export default {
        components: {
            JetActionMessage,
            JetButton,
            JetFormSection,
            JetInput,
            JetInputError,
            JetLabel,
            JetSecondaryButton,
        },

        props: ['user'],

        data() {
            const pageProps = (this.$page && (this.$page.props || this.$page)) || {}
            const currentUser = this.user || pageProps.user || {}

            return {
                form: {
                    '_method': 'PUT',
                    name: currentUser.name || '',
                    email: currentUser.email || '',
                    photo: null,
                    processing: false,
                    recentlySuccessful: false,
                },
                formErrors: {},
                photoPreview: null,
                photoValidationMessage: '',
                photoValidationState: null,
            }
        },

        methods: {
            fieldError(field) {
                return this.formErrors[field] || '';
            },

            updateProfileInformation() {
                if (this.$refs.photo) {
                    this.form.photo = this.$refs.photo.files[0]
                }

                const data = new FormData();
                data.append('_method', 'PUT');
                data.append('name', this.form.name);
                data.append('email', this.form.email);

                if (this.form.photo) {
                    data.append('photo', this.form.photo);
                }

                this.form.processing = true;
                this.formErrors = {};
                this.form.recentlySuccessful = false;

                this.$inertia.post(route('user-profile-information.update'), data, {
                    preserveScroll: true,
                    onError: (errors) => {
                        this.formErrors = errors || {};
                    },
                    onSuccess: () => {
                        this.form.recentlySuccessful = true;
                        this.form.photo = null;
                    },
                    onFinish: () => {
                        this.form.processing = false;
                    },
                });
            },

            selectNewPhoto() {
                this.$refs.photo.click();
            },

            updatePhotoPreview() {
                const reader = new FileReader();

                reader.onload = (e) => {
                    this.photoPreview = e.target.result;
                };

                reader.readAsDataURL(this.$refs.photo.files[0]);
            },

            deletePhoto() {
                this.$inertia.delete(route('current-user-photo.destroy'), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.photoPreview = null
                    },
                });
            },

            formError(field) {
                if (this.form && typeof this.form.error === 'function') {
                    return this.form.error(field)
                }

                return (this.form && this.form.errors && this.form.errors[field]) || null
            },
        },
        computed: {
            pageProps() {
                return (this.$page && (this.$page.props || this.$page)) || {}
            },

            safeJetstream() {
                return this.pageProps.jetstream || {}
            },

            currentUser() {
                return this.user || this.pageProps.user || {}
            },

            profilePhotoSrc() {
                const user = this.currentUser
                return user.id ? `/member/photo/${user.id}?v=${Date.now()}` : (user.profile_photo_url || '')
            },

            photoValidationClass() {
                if (this.photoValidationState === 'success') {
                    return 'text-green-600'
                }
                if (this.photoValidationState === 'error') {
                    return 'text-red-600'
                }
                return 'text-gray-500'
            },
        },
    }
</script>
